import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { isEmpty, isNil } from "ramda";

const DoBLineChart = ({
  prefix,
  data,
  environmentalData,
  measureName,
  withToolbox = false,
  resetZoomHandler = () => {},
  pointsSelectHandler = () => {},
  startZoom = 0,
  endZoom = 100,
  startIndex,
  endIndex,
  isAlert,
  alertMeasure,
  afterAlert,
  assetModule,
  assetInfo,
  configData,
  setAfterAlert,
  alertLines,
  setAlertLines,
  setLoadingAverage,
  setLoadingAverageTemporal,
  info,
  manualPk,

  selectTemporal,
  intl,
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  useEffect(() => {
    let lastRatio = window.devicePixelRatio;

    const interval = setInterval(() => {
      if (window.devicePixelRatio !== lastRatio) {
        lastRatio = window.devicePixelRatio;
        chartInstanceRef.current?.resize();
      }
    }, 300);

    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const chartDom = chartRef.current;
    const myChart = echarts.init(chartDom);
    chartInstanceRef.current = myChart;

    // Aquí deberías poner tus opciones del gráfico
    // myChart.setOption({...});

    // Observa cambios de tamaño del contenedor, incluso por zoom
    const resizeObserver = new ResizeObserver(() => {
      myChart.resize();
    });

    resizeObserver.observe(chartDom);

    return () => {
      resizeObserver.disconnect();
      myChart.dispose();
    };
  }, []);
  const selectTemporalRef = useRef(selectTemporal);

  useEffect(() => {
    selectTemporalRef.current = selectTemporal;
  }, [selectTemporal]);
  const dateOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };

  useEffect(() => {
    const chartElement = document.getElementById(`${prefix}-stacked-line-chart`);
    let myChart = echarts.getInstanceByDom(chartElement);

    if (!myChart) {
      myChart = echarts.init(chartElement);
    }
    let minData = Number.POSITIVE_INFINITY;
    let maxData = Number.NEGATIVE_INFINITY;
    let minBathymetry = Number.POSITIVE_INFINITY;
    let maxBathymetry = Number.NEGATIVE_INFINITY;
    const mainMeasurand = data?.measurand;
    data?.series?.forEach((series) => {
      series?.data?.forEach((value) => {
        if (!isNil(data) && !contieneTemporaEnParentesis(data.title)) {
          if (value[1] < minData) minData = value[1];
          if (value[1] > maxData) maxData = value[1];
        } else {
          if (value[1] < minData) minData = value[1];
          if (value[1] > maxData) maxData = value[1];
        }
      });
    });
    data?.bathymetry?.forEach((series, index) => {
      series?.data?.forEach((value) => {
        if (!isNil(data) && !contieneTemporaEnParentesis(data.title)) {
          if (value[1] < minBathymetry) minBathymetry = value[1];
          if (value[1] > maxBathymetry) maxBathymetry = value[1];
        }
      });
    });
    const xAxisData =
      (data?.xAxis?.data?.length || 0) > (environmentalData?.xAxis?.data?.length || 0) ? data : environmentalData || {};

    let combinado = null;
    if (!isNil(data) && !isNil(environmentalData) && contieneTemporaEnParentesis(data.title)) {
      combinado = data?.series[0].data.concat(environmentalData?.series[0].data);
      combinado.sort((a, b) => new Date(a[0]) - new Date(b[0]));
      combinado = combinado.map((subArray) => subArray[0]);
    }
    const dataMinRounded = Math.floor(minData * 100) / 100;
    const dataMaxRounded = Math.ceil(maxData * 100) / 100;
    const moduleData = configData.modules.find((module) => module.name === assetModule);
    const packageData = moduleData.packages.find((packg) => packg.name === measureName);

    const colors = [
      "#5470C6", // Azul fuerte
      "#91CC75", // Verde suave
      "#EE6666", // Rojo intenso
      "#9A60B4", // Púrpura
      "#FAC858", // Amarillo dorado
      "#EA7CCC", // Rosa claro
      "#FC8452", // Naranja
      "#73C0DE", // Azul cielo
      "#3BA272", // Verde bosque
      "#FF5733", // Naranja rojizo
      "#33FF57", // Verde brillante
      "#5733FF", // Azul eléctrico
      "#FFD700", // Oro
      "#40E0D0", // Turquesa
      "#8A2BE2", // Violeta
      "#FF69B4", // Rosa fuerte
      "#7FFF00", // Chartreuse
      "#DC143C", // Carmesí
      "#00CED1", // Azul oscuro
      "#4682B4", // Acero azul
      "#32CD32", // Verde lima
      "#FF4500", // Naranja rojizo
      "#9400D3", // Violeta oscuro
      "#20B2AA", // Verde aguamarina
      "#FFDAB9", // Melocotón
      "#B22222", // Rojo ladrillo
      "#00FA9A", // Verde primavera
      "#191970", // Azul medianoche
      "#ADFF2F", // Verde amarillo
    ];
    const legend = [];
    const auxMeasurands = [];
    const extraAxes = {};
    if (contieneTemporaEnParentesis(data.title)) {
      for (let index = 0; index < data?.series.length; index++) {
        legend.push(
          `${intl.formatMessage({ id: data.series[index].title })} ${
            data.series[index].title === measureName ? data.series[index].name.toFixed(2) : ""
          }`,
        );
        if (data.series[index].title !== measureName && data.series[index].name !== "Average") {
          auxMeasurands.push(data.series[index].title);
        }
      }
      //for (let index = 0; index < environmentalData?.series.length; index++) {
      //  legend.push(
      //    environmentalData.series[index].title
      //      .split("_") // Divide la cadena en partes donde hay '_'
      //      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitaliza la primera letra de cada palabra
      //      .join(" "),
      //  );
      //}
    }
    const finalYAxis = [];
    const keys = Object.keys(extraAxes);
    for (let indexKey = 0; indexKey < keys.length; indexKey++) {
      finalYAxis.push({
        type: "value",
        min: extraAxes[keys[indexKey]][0],
        max: extraAxes[keys[indexKey]][1],
        name: `${intl.formatMessage({ id: keys[indexKey] })} (${extraAxes[keys[indexKey]][2]})`,
        nameRotate: 270,
        nameLocation: "middle",
        nameGap: 35,
      });
    }
    const options = {
      color: colors,
      animation: false,
      tooltip: !contieneTemporaEnParentesis(data.title)
        ? {
            trigger: "axis",
            confine: true,
            appendToBody: true,
            valueFormatter: (value) => value?.toFixed?.(2),
            position: function (pos, params, dom, rect, size) {
              const [x, y] = pos;
              const viewWidth = size.viewSize[0];
              const viewHeight = size.viewSize[1];
              const boxWidth = size.contentSize[0];
              const boxHeight = size.contentSize[1];

              let finalX = x;
              let finalY = y;

              if (x + boxWidth > viewWidth) {
                finalX = x - boxWidth;
              }
              if (finalX < 0) {
                finalX = 0;
              }

              if (y + boxHeight > viewHeight) {
                finalY = y - boxHeight;
              }
              if (finalY < 0) {
                finalY = 0;
              }

              return [finalX, finalY];
            },
          }
        : null,
      legend: {
        data: data.legend,
        top: data?.series?.length + data?.zones?.length + data?.bathymetry?.length > 3 ? "7%" : "0%",
        type: "scroll",
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "7%",
        containLabel: true,
      },
      toolbox: withToolbox
        ? {
            feature: {
              dataZoom: {
                yAxisIndex: "all",
                xAxisIndex: "all",
                brushStyle: {
                  color: "#5470C655",
                  borderType: "solid",
                  borderWidth: 1,
                  borderColor: "#5470C6",
                  shadowColor: "#5470C6",
                },
                title: {
                  zoom: intl.formatMessage({ id: "Zoom in" }),
                  back: intl.formatMessage({ id: "Zoom out" }),
                },
              },
              restore: {
                title: intl.formatMessage({ id: "Restore zoom" }),
              },
              saveAsImage: {
                title: intl.formatMessage({ id: "Save as image" }),
              },
              myCustomCSVDownload: {
                show: true,
                title: intl.formatMessage({ id: "Download CSV" }),
                icon: "image://csv-icon80.png",
                onclick: function () {
                  !isNil(environmentalData) && !isEmpty(environmentalData) ? downloadTemporalCSV() : downloadCSV();
                },
              },
            },
          }
        : {},
      xAxis: {
        type: !contieneTemporaEnParentesis(data.title) ? "value" : "time",
        max: xAxisData?.xAxis?.data[xAxisData?.xAxis?.data.length - 1],
        data: !contieneTemporaEnParentesis(data.title)
          ? xAxisData?.xAxis?.data.map((num) => {
              return typeof num === "number" ? parseFloat(num.toFixed(1)) : num;
            })
          : !isNil(combinado)
            ? combinado.map((date) => {
                return new Date(date).toLocaleString("es-ES", dateOptions);
              })
            : null,
        boundaryGap: false,
        splitLine: {
          show: false,
        },
        axisLine: { onZero: false },
        name: !contieneTemporaEnParentesis(data.title) ? "PK (m)" : `${intl.formatMessage({ id: "time" })}`,
        nameLocation: "middle", // esto centrará el nombre en el eje
        nameGap: 25,
        axisLabel: contieneTemporaEnParentesis(data.title)
          ? {
              interval: "auto",
              hideOverlap: true,
              formatter: (value, index, ticks) => {
                const date = new Date(value);

                // Accedemos al eje X de tipo "time" para estimar el rango visible
                const axis = myChart?.getModel?.()?.getComponent("xAxis")?.axis;
                const extent = axis?.scale._extent || [0, 0];
                const rangeMs = Math.abs(extent[1] - extent[0]); // milisegundos visibles

                // Determinar el nivel de detalle según el rango temporal visible
                const oneDay = 24 * 60 * 60 * 1000;
                const oneMonth = 30 * oneDay;
                const oneYear = 365 * oneDay;

                // Formato progresivo según zoom
                if (rangeMs > 3 * oneYear) {
                  return date.toLocaleString("es-ES", { year: "numeric" });
                } else if (rangeMs > 6 * oneMonth) {
                  return date.toLocaleString("es-ES", {
                    month: "short",
                    year: "numeric",
                  });
                } else if (rangeMs > 5 * oneDay) {
                  return date.toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "short",
                  });
                } else if (rangeMs > oneDay / 2) {
                  return date.toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                } else {
                  return date.toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  });
                }
              },
            }
          : {},
      },
      yAxis: [
        {
          type: "value",
          min: dataMinRounded === Infinity ? null : dataMinRounded,
          max: dataMaxRounded === -Infinity ? null : dataMaxRounded,
          name: `${intl.formatMessage({ id: measureName })} (m)`,
          nameRotate: 90,
          nameLocation: "middle",
          nameGap: 35,
          axisLine: {
            show: true,
            lineStyle: {
              color: colors[0],
            },
          },
        },

        {
          type: "value",
          min: minBathymetry,
          max: maxBathymetry,
          name: `${intl.formatMessage({ id: "bathymetry" })} (m)`,
          nameRotate: 270,
          nameGap: 35,
          nameLocation: "middle",
        },
      ],
      //dataZoom: [
      //  {
      //    type: "inside",
      //    start: 0,
      //    end: 100,
      //    yAxisIndex: [0],
      //    filterMode: "none",
      //  },
      //],
      series: [
        ...(data?.series?.map((s) => ({
          ...s,
          sampling: "lttb",
          symbol: "none",
        })) || []),

        ...(data?.bathymetry?.map((s, index) => ({
          ...s,
          name: s.name,
          type: "line",
          sampling: "lttb",
          symbol: "none",
          symbolSize: 1,
          yAxisIndex: 1,
          color: colors[index + (data?.series?.length || 0)],
        })) || []),
        ...(data?.zones?.map((s) => ({
          ...s,
        })) || []),
      ],
    };
    console.log(options);
    myChart.setOption(options, { notMerge: false, lazyUpdate: true, silent: true });
    if (!isNil(alertLines) && alertLines.start !== null && alertLines.end !== null) {
      const zoomLines = [
        {
          name: "Zoom Start",
          xAxis: alertLines.start,
          lineStyle: {
            color: "red",
            width: 2,
            type: "solid",
          },
          label: { show: false },
        },
        {
          name: "Zoom End",
          xAxis: alertLines.end,
          lineStyle: {
            color: "red",
            width: 2,
            type: "solid",
          },
          label: { show: false },
        },
      ];

      myChart.setOption(
        {
          series:
            data?.series?.map((s) => ({
              ...s,
              type: "line",
              markLine: {
                symbol: "none",
                data: zoomLines,
              },
            })) || [], // Asegúrate de que sea un array vacío si no hay series
        },
        { notMerge: false, lazyUpdate: true, silent: true },
      );
    }
    const addZoomLines = (startValue, endValue) => {
      const zoomLines = [
        {
          name: "Zoom Start",
          xAxis: startValue,
          lineStyle: {
            color: "red",
            width: 2,
            type: "solid",
          },
          label: { show: false },
        },
        {
          name: "Zoom End",
          xAxis: endValue,
          lineStyle: {
            color: "red",
            width: 2,
            type: "solid",
          },
          label: { show: false },
        },
      ];

      myChart.setOption(
        {
          series: data?.series?.map((s) => ({
            ...s,
            markLine: {
              symbol: "none",
              data: zoomLines,
            },
          })),
        },
        { notMerge: false, lazyUpdate: true, silent: true },
      );
    };

    //myChart.on("click", (params) => {
    //  onChartClick(params);
    //  let i = -Infinity;
    //  let j = Infinity;
    //  addZoomLines(i, j);
    //});
    myChart.on("dataZoom", (params) => {
      if (
        params &&
        params.batch &&
        params.batch.length > 0 &&
        !isNil(params.batch[0].startValue) &&
        !isNil(params.batch[0].endValue) &&
        selectTemporalRef.current
      ) {
        let i = params.batch[0].startValue;
        let j = params.batch[0].endValue;
        if (j - i >= 64) {
          let centerPoint = parseInt((j - i) / 2) + i;
          i = centerPoint >= 32 ? centerPoint - 32 : 0;
          centerPoint += 32;
          const count = data?.series?.[0]?.data?.length ?? 0;
          j = centerPoint < count ? centerPoint : count - 1;
        }
        if (!contieneTemporaEnParentesis(data.title)) {
          addZoomLines(i, j);
        }
        onDataZoom(params, data.series[0].data.length);
      }
    });
    myChart.on("restore", () => {
      resetZoomHandler();
    });
    myChart.on("mousewheel", function (params) {
      params.event.preventDefault(); // Evita el zoom con la rueda del ratón
    });

    return () => {
      setLoadingAverage(false);
      setLoadingAverageTemporal(false);
      myChart.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, environmentalData, prefix, startZoom, endZoom]);

  useEffect(() => {
    if (!isNil(startIndex) && !isNil(endIndex) && isAlert && !(measureName === "strain" && alertMeasure === "leak")) {
      const chartElement = document.getElementById(`${prefix}-stacked-line-chart`);
      const myChart = echarts.init(chartElement);
      let i = startIndex;
      let j = endIndex;
      if (j - i >= 64) {
        let centerPoint = parseInt((j - i) / 2) + i;
        i = centerPoint >= 32 ? centerPoint - 32 : 0;
        centerPoint += 32;
        const count = data?.series?.[0]?.data?.length ?? 0;
        j = centerPoint < count ? centerPoint : count - 1;
      }
      if (!contieneTemporaEnParentesis(data.title)) {
        const zoomLines = [
          {
            name: "Zoom Start",
            xAxis: i,
            lineStyle: {
              color: "red",
              width: 2,
              type: "solid",
            },
            label: { show: false },
          },
          {
            name: "Zoom End",
            xAxis: j,
            lineStyle: {
              color: "red",
              width: 2,
              type: "solid",
            },
            label: { show: false },
          },
        ];
        setAlertLines({ start: i, end: j });

        const iniZoom = Math.floor((startIndex * 100) / data.xAxis.data.length) - 1;
        const finZoom = Math.floor((endIndex * 100) / data.xAxis.data.length) + 2;

        myChart.setOption(
          {
            series: data?.series?.map((s) => ({
              ...s,
              markLine: {
                symbol: "none",
                data: zoomLines,
              },
            })),
            dataZoom: [
              {
                type: "inside",
                start: iniZoom,
                end: finZoom,
              },
            ],
          },
          { notMerge: false, lazyUpdate: true, silent: true },
        );
        if (!afterAlert && !isNil(alertMeasure)) pointsSelectHandler(startIndex, endIndex, false);
        if (isNil(alertMeasure) && !afterAlert) pointsSelectHandler(startIndex, endIndex, afterAlert);
      }
    } else if (!isNil(startIndex) && !isNil(endIndex) && isAlert) {
      if (!afterAlert) pointsSelectHandler(startIndex, endIndex, false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIndex]);
  function isFloat(value) {
    return !isNaN(value) && value.toString().indexOf(".") !== -1;
  }

  function downloadCSV() {
    //asset, equipment, channel, dateTime/PK
    const header = [
      info.asset,
      info.equipment.name,
      info.equipment.channels[info.channel - 1].name,
      isFloat(data?.series?.[0]?.name) || Number.isInteger(data?.series?.[0]?.name)
        ? data?.series?.[0]?.name.toFixed(2)
        : info.dateTime,
    ].join(",");
    const subHeader = [
      isFloat(data?.series?.[0]?.name) || Number.isInteger(data?.series?.[0]?.name)
        ? intl.formatMessage({ id: "date" })
        : "PK",
      intl.formatMessage({ id: measureName }),
    ].join(",");
    let dateTime, combinedData;
    if (isFloat(data?.series?.[0]?.name) || Number.isInteger(data?.series?.[0]?.name)) {
      dateTime = data?.series?.[0]?.name.toFixed(2);
      const timeData = data?.xAxis?.data;
      const serialData = data?.series?.[0]?.data;
      combinedData = timeData?.map((date, index) => [date.replace(" ", "T"), serialData[index][1].toFixed(2)]);
    } else {
      dateTime = data?.series?.[0]?.name?.replace(" ", "T");
      const pkData = data?.xAxis?.data;
      const serialData = data?.series?.[0]?.data;
      combinedData = pkData?.map((pk, index) => [pk.toFixed(2), serialData[index][1].toFixed(2)]);
    }
    const csvData = [header, subHeader, ...combinedData].join("\n");

    const blob = new Blob([csvData], { type: "text/csv" });

    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${dateTime}_${measureName}.csv`;

    a.click();

    URL.revokeObjectURL(a.href);
  }
  function downloadTemporalCSV() {
    const header = [
      info.asset,
      info.equipment.name,
      info.equipment.channels[info.channel - 1].name,
      data?.series?.[0]?.name.toFixed(2),
    ].join(",");
    const subHeader = [intl.formatMessage({ id: "date" }), intl.formatMessage({ id: measureName })].join(",");
    let dateTime, combinedData;
    dateTime = data?.series?.[0]?.name;
    const timeData = data?.xAxis?.data;
    const serialData = data?.series?.[0]?.data;
    combinedData = timeData?.map((date, index) => [date.replace(" ", "T"), serialData[index][1].toFixed(2)]);

    const csvData = [header, subHeader, ...combinedData].join("\n");

    const blob = new Blob([csvData], { type: "text/csv" });

    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${dateTime.toFixed(2)}_${measureName}.csv`;

    a.click();

    URL.revokeObjectURL(a.href);
    for (let envIndex = 0; envIndex < environmentalData?.series?.length; envIndex++) {
      const subHeaderEnv = [intl.formatMessage({ id: "date" }), environmentalData.series[envIndex].title].join(",");
      dateTime = data?.series?.[0]?.name;
      const timeData = data?.xAxis?.data;
      const serialData = environmentalData?.series?.[envIndex]?.data;
      combinedData = timeData?.map((date, index) => [date.replace(" ", "T"), serialData[index][1].toFixed(2)]);
      const envCsvData = [header, subHeaderEnv, ...combinedData].join("\n");

      const blob = new Blob([envCsvData], { type: "text/csv" });

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${environmentalData.series[envIndex].title}_${dateTime}_${intl.formatMessage({
        id: "intensity",
      })}.csv`;

      a.click();

      URL.revokeObjectURL(a.href);
    }
  }
  function contieneTemporaEnParentesis(cadena) {
    const regexParentesis = /\(.*temporal.*\)/i;

    return regexParentesis.test(cadena);
  }

  const onDataZoom = (params, length) => {
    if (
      !isNil(params?.batch) &&
      !isEmpty(params.batch.length) &&
      !isNil(params.batch?.[0]?.startValue) &&
      !isNil(params.batch?.[0]?.endValue)
    ) {
      const startIndex2 = params.batch[0].startValue;
      const endIndex2 = params.batch[0].endValue;
      pointsSelectHandler(startIndex2, endIndex2, afterAlert);
    }
  };

  return (
    <div
      id={`${prefix}-stacked-line-chart`}
      ref={chartRef}
      style={{
        width: "100%",
        height: !contieneTemporaEnParentesis(data.title) ? "110%" : "40vh",
        minWidth: 0,
        position: "relative",
        overflow: "hidden",
      }}
    />
  );
};

export default DoBLineChart;
